import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ComingSoonBanner from '../components/ui/ComingSoonBanner';

interface LegalDocument {
  id: string;
  title: string;
  description: string;
  category: string;
  type: 'template' | 'form' | 'guide';
  complexity: 'simple' | 'medium' | 'complex';
  estimatedTime: string;
  isFree: boolean;
  price?: string;
  icon: string;
}

const LegalDocumentsPage: React.FC = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const legalDocuments: LegalDocument[] = [
    {
      id: '1',
      title: 'Cease and Desist Letter',
      description: 'Professional letter to stop harassment, copyright infringement, or other unwanted behavior.',
      category: 'Business',
      type: 'template',
      complexity: 'simple',
      estimatedTime: '10-15 minutes',
      isFree: true,
      icon: '📄'
    },
    {
      id: '2',
      title: 'Employment Contract',
      description: 'Comprehensive employment agreement template with standard clauses and protections.',
      category: 'Employment',
      type: 'template',
      complexity: 'complex',
      estimatedTime: '30-45 minutes',
      isFree: false,
      price: '$29.99',
      icon: '📋'
    },
    {
      id: '3',
      title: 'Non-Disclosure Agreement',
      description: 'Protect your confidential information with this legally binding NDA template.',
      category: 'Business',
      type: 'template',
      complexity: 'medium',
      estimatedTime: '15-20 minutes',
      isFree: false,
      price: '$19.99',
      icon: '🤐'
    },
    {
      id: '4',
      title: 'Small Claims Court Filing',
      description: 'Complete guide and forms for filing a small claims court case.',
      category: 'Litigation',
      type: 'guide',
      complexity: 'medium',
      estimatedTime: '20-30 minutes',
      isFree: true,
      icon: '⚖️'
    },
    {
      id: '5',
      title: 'Power of Attorney',
      description: 'Legal document granting authority to act on your behalf in specific matters.',
      category: 'Estate Planning',
      type: 'template',
      complexity: 'complex',
      estimatedTime: '25-35 minutes',
      isFree: false,
      price: '$39.99',
      icon: '👤'
    },
    {
      id: '6',
      title: 'Rental Agreement',
      description: 'Standard residential lease agreement with customizable terms.',
      category: 'Real Estate',
      type: 'template',
      complexity: 'medium',
      estimatedTime: '20-25 minutes',
      isFree: false,
      price: '$24.99',
      icon: '🏠'
    },
    {
      id: '7',
      title: 'Divorce Petition',
      description: 'Step-by-step guide and forms for filing for divorce.',
      category: 'Family Law',
      type: 'guide',
      complexity: 'complex',
      estimatedTime: '45-60 minutes',
      isFree: false,
      price: '$49.99',
      icon: '💔'
    },
    {
      id: '8',
      title: 'Business Formation Guide',
      description: 'Complete guide to forming an LLC, Corporation, or Partnership.',
      category: 'Business',
      type: 'guide',
      complexity: 'complex',
      estimatedTime: '60-90 minutes',
      isFree: false,
      price: '$79.99',
      icon: '🏢'
    },
    {
      id: '9',
      title: 'Demand Letter',
      description: 'Professional demand letter template for debt collection or dispute resolution.',
      category: 'Business',
      type: 'template',
      complexity: 'simple',
      estimatedTime: '10-15 minutes',
      isFree: true,
      icon: '📝'
    },
    {
      id: '10',
      title: 'Will Template',
      description: 'Last will and testament template with comprehensive estate planning guidance.',
      category: 'Estate Planning',
      type: 'template',
      complexity: 'complex',
      estimatedTime: '40-50 minutes',
      isFree: false,
      price: '$59.99',
      icon: '📜'
    },
    {
      id: '11',
      title: 'Settlement Agreement',
      description: 'Template for resolving disputes and claims through settlement.',
      category: 'Litigation',
      type: 'template',
      complexity: 'medium',
      estimatedTime: '25-35 minutes',
      isFree: false,
      price: '$34.99',
      icon: '🤝'
    },
    {
      id: '12',
      title: 'Trademark Application Guide',
      description: 'Complete guide to filing a trademark application with the USPTO.',
      category: 'Intellectual Property',
      type: 'guide',
      complexity: 'complex',
      estimatedTime: '90-120 minutes',
      isFree: false,
      price: '$89.99',
      icon: '™️'
    }
  ];

  const categories = ['all', 'Business', 'Employment', 'Litigation', 'Estate Planning', 'Real Estate', 'Family Law', 'Intellectual Property'];
  const types = ['all', 'template', 'form', 'guide'];

  const filteredDocuments = legalDocuments.filter(doc => {
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
    const matchesType = selectedType === 'all' || doc.type === selectedType;
    const matchesSearch = !searchTerm || 
      doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.category.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCategory && matchesType && matchesSearch;
  });

  const getComplexityColor = (complexity: string) => {
    switch (complexity) {
      case 'simple': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'complex': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'template': return 'bg-blue-100 text-blue-800';
      case 'form': return 'bg-purple-100 text-purple-800';
      case 'guide': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('legalDocuments.title', 'Legal Documents & Templates')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('legalDocuments.subtitle', 'Access professional legal document templates, forms, and guides. Save time and money with our comprehensive legal document library.')}
          </p>
        </div>

        {/* Coming Soon Banner */}
        <ComingSoonBanner />

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('legalDocuments.search', 'Search')}
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('legalDocuments.searchPlaceholder', 'Search documents...')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('legalDocuments.category', 'Category')}
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? t('legalDocuments.allCategories', 'All Categories') : category}
                  </option>
                ))}
              </select>
            </div>

            {/* Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('legalDocuments.type', 'Type')}
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
              >
                {types.map(type => (
                  <option key={type} value={type}>
                    {type === 'all' ? t('legalDocuments.allTypes', 'All Types') : 
                     type === 'template' ? t('legalDocuments.template', 'Template') :
                     type === 'form' ? t('legalDocuments.form', 'Form') :
                     t('legalDocuments.guide', 'Guide')}
                  </option>
                ))}
              </select>
            </div>

            {/* Results Count */}
            <div className="flex items-end">
              <p className="text-sm text-gray-600">
                {t('legalDocuments.results', '{{count}} documents', { count: filteredDocuments.length })}
              </p>
            </div>
          </div>
        </div>

        {/* Documents Grid */}
        {filteredDocuments.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📄</div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              {t('legalDocuments.noResults', 'No documents found')}
            </h3>
            <p className="text-gray-600">
              {t('legalDocuments.noResultsDescription', 'Try adjusting your search criteria or filters.')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDocuments.map((document) => (
              <div key={document.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span className="text-2xl mr-3">{document.icon}</span>
                        <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                          {document.title}
                        </h3>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        {document.category}
                      </p>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {document.description}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(document.type)}`}>
                      {t(`legalDocuments.type.${document.type}`, document.type)}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getComplexityColor(document.complexity)}`}>
                      {t(`legalDocuments.complexity.${document.complexity}`, document.complexity)}
                    </span>
                  </div>

                  {/* Details */}
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">{t('legalDocuments.estimatedTime', 'Est. Time')}:</span>
                      <span className="text-gray-900">{document.estimatedTime}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">{t('legalDocuments.price', 'Price')}:</span>
                      <span className="text-gray-900 font-medium">
                        {document.isFree ? t('legalDocuments.free', 'Free') : document.price}
                      </span>
                    </div>
                  </div>

                  {/* Action Button */}
                  <button
                    className={`w-full py-2 px-4 rounded-md transition-colors duration-200 text-center block ${
                      document.isFree 
                        ? 'bg-green-600 text-white hover:bg-green-700' 
                        : 'bg-cyan-600 text-white hover:bg-cyan-700'
                    }`}
                  >
                    {document.isFree 
                      ? t('legalDocuments.downloadFree', 'Download Free') 
                      : t('legalDocuments.purchase', 'Purchase')
                    }
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-lg p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-4">
            {t('legalDocuments.ctaTitle', 'Need a custom legal document?')}
          </h2>
          <p className="text-lg mb-6 opacity-90">
            {t('legalDocuments.ctaDescription', 'Our AI-powered platform can help you create personalized legal documents tailored to your specific needs.')}
          </p>
          <Link
            to="/file-lawsuit"
            className="bg-white text-cyan-600 px-6 py-3 rounded-md font-medium hover:bg-gray-100 transition-colors duration-200"
          >
            {t('legalDocuments.createCustom', 'Create Custom Document')}
          </Link>
        </div>

        {/* Features Section */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="text-4xl mb-4">⚡</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {t('legalDocuments.feature1.title', 'Fast & Easy')}
            </h3>
            <p className="text-gray-600">
              {t('legalDocuments.feature1.description', 'Complete legal documents in minutes, not hours.')}
            </p>
          </div>
          <div className="text-center">
            <div className="text-4xl mb-4">🛡️</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {t('legalDocuments.feature2.title', 'Legally Sound')}
            </h3>
            <p className="text-gray-600">
              {t('legalDocuments.feature2.description', 'All documents are reviewed by legal professionals.')}
            </p>
          </div>
          <div className="text-center">
            <div className="text-4xl mb-4">💰</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {t('legalDocuments.feature3.title', 'Cost Effective')}
            </h3>
            <p className="text-gray-600">
              {t('legalDocuments.feature3.description', 'Save thousands compared to hiring a lawyer.')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalDocumentsPage;